import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, column, indexView, loader, pagination, Tag, tag } from '@/ui/components';
import { bulkDelete, list as getRoleList, remove } from '@/services/api/resources/role';
import { emptyList } from '@/services/api';
import { RoleList, RoleRecord } from '@/entities/role';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { bulkDeleteButton } from '@/ui/components/Button';
import { dataNameProp } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { tagColumn } from '@/ui/components/table/TableColumn';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import * as qs from '@/ui/queryState';
import { searchFilter } from '@/ui/components/filters/filters-horizontal';
import { permission } from '@/ui/dictionary';
import { filterFormHorizontal } from '@/ui/components/Columns';
import { SortDirection } from '@/ui/types';
import { useRoleCheck } from '@/ui/pages/role/useRoleCheck';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import RoleEmpty from '@/ui/pages/role/RoleEmpty.vue';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';

export const RoleIndexPage = defineComponent({
  name: 'RoleIndexPage',
  setup() {
    if (!hasFeature('roles')) {
      return renderResourceTeaserPage(t('common.roles'), t('role.teaser'), t('common.teaser'), 'roleIndex');
    }

    if (!hasPermission(Permissions.MANAGE_PERMISSIONS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const search = qs.string('search', '');

    // state stored in query string
    const { page, perPage } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useRoleCheck();

    // get list task
    const list = ref<RoleList>(emptyList());
    const getList = useTask(
      () => getRoleList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        sort: sort.value,
        dir: dir.value,
      }),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, search], getList.run);

    const { selectedResources, deleteBulkTask, onCheckedRowsUpdate } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
    });

    return () => loader('div', performCheck, [
      checkResult.value ? indexView(
        [
          filterFormHorizontal([
            bulkDeleteButton({ resources: selectedResources }),
            searchFilter(search),
          ]),
        ],
        [
          box([
            loader('div', getList, [
              h(BulkDeletableTable, {
                props: {
                  editRoute: 'roleEdit',
                  list: list.value.data,
                  deleteFn: remove,
                  selectedRecords: selectedResources.value,
                  loading: deleteBulkTask.state.value.pending,
                  paginationLinks: list.value.meta,
                  isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                  recordName: {
                    plural: t('common.roles'),
                    singular: t('common.role'),
                  },
                },
                on: {
                  'update:checkedRows': onCheckedRowsUpdate,
                },
                scopedSlots: {
                  title: ({ row: role }: { row: RoleRecord }) => role.data.title,
                  default: () => [
                    column({
                      field: 'permissions',
                      label: t('common.permissions'),
                    }, {
                      default: ({ row: role }: { row: RoleRecord }) => role.data.permissions
                        .filter(perm => perm.length > 0 && permission(perm as Permissions))
                        .map(perm => tag('neutral', permission(perm as Permissions))),
                    }),
                    tagColumn({
                      field: 'users',
                      label: t('common.users'),
                    }, {
                      default: ({ row: role }: { row: RoleRecord }) => [
                        role.data.users.slice(0, 15).map(dataNameProp).join(', '),
                        role.data.users.length > 15 ? h(
                          Tag,
                          { class: 'tag--users', props: { type: 'neutral' } },
                          `+${t('roles.users-count', undefined, role.data.users.length - 15)}`,
                        ) : null,
                      ],
                    }),
                  ],
                },
              }),
              pagination({
                current: page.value,
                total: list.value.meta.total,
                perPage: perPage.value,
              }),
            ], !initialLoading.value),
          ]),
        ],
      ) : box([h(RoleEmpty)]),
    ], false);
  },
});
